import React, { useState, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  Users,
  MessageCircle,
  Headphones,
  Megaphone,
  Gauge,
  TrendingUp,
  Layers,
  Sun,
  Moon,
  DollarSign,
  Radio,
  Newspaper,
  AlertTriangle,
  Settings as SettingsIcon,
  Menu,
  X,
  LogOut,
  RotateCcw,
  Activity,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useKeyboardOpen } from '../hooks/useKeyboardOpen.js';
import SurveyRatingChip from './SurveyRatingChip.jsx';
import ServerPaymentsChip from './ServerPaymentsChip.jsx';

const navigation = [
  { name: 'Дашборд', href: '/', icon: BarChart3 },
  { name: 'Статистика', href: '/statistics', icon: TrendingUp },
  { name: 'Пользователи', href: '/users', icon: Users },
  { name: 'Чаты', href: '/messenger', icon: MessageCircle },
  { name: 'Обращения', href: '/appeals', icon: Headphones },
  { name: 'Рассылки', href: '/broadcasts', icon: Megaphone },
  { name: 'Аналитика рассылок', href: '/broadcast-analytics', icon: Gauge },
  { name: 'Мониторинг', href: '/monitoring', icon: Radio },
  { name: 'Новости', href: '/news', icon: Newspaper },
  { name: 'Аварии', href: '/emergency', icon: AlertTriangle },
  { name: 'Бухгалтерия', href: '/accounting', icon: DollarSign },
  { name: 'Приложения', href: '/apps', icon: Layers },
  // Новые пункты добавлять В КОНЕЦ: MOBILE_PRIMARY_INDEXES ниже завязан на индексы.
  { name: 'Возврат дней', href: '/winback', icon: RotateCcw },
  { name: 'События', href: '/events', icon: Activity },
];

// На мобильной панели: 5 основных + «Ещё» (всплывающее окно с остальными)
const MOBILE_PRIMARY_INDEXES = [0, 1, 2, 3, 4]; // Дашборд, Статистика, Пользователи, Чаты, Обращения
const mobilePrimary = MOBILE_PRIMARY_INDEXES.map((i) => navigation[i]);
const mobileMore = navigation.filter((_, i) => !MOBILE_PRIMARY_INDEXES.includes(i));

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const keyboardOpen = useKeyboardOpen();

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Guard от повторного вызова: Telegram WebApp допускает только ОДИН попап за раз,
  // и showConfirm бросает СИНХРОННОЕ исключение WebAppPopupOpened, если попап уже
  // открыт (двойной клик / залипший попап) — без try/catch это роняло весь UI.
  const loggingOut = useRef(false);

  const handleLogout = () => {
    if (loggingOut.current) return;
    const msg = 'Выйти из админки?';
    const tg = window.Telegram?.WebApp;
    if (tg?.showConfirm) {
      loggingOut.current = true;
      try {
        tg.showConfirm(msg, (ok) => {
          loggingOut.current = false;
          if (ok) logout();
        });
        // Подстраховка: если Telegram не вызовет колбэк (свайп-закрытие) — снять флаг.
        setTimeout(() => { loggingOut.current = false; }, 8000);
      } catch {
        // Попап занят/не поддерживается — не валим UI, выходим сразу (клик намеренный).
        loggingOut.current = false;
        logout();
      }
    } else if (window.confirm(msg)) {
      logout();
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <header className={`bg-white dark:bg-gray-800 shadow-sm border-b border-primary-200 dark:border-gray-700 sticky top-0 z-30 ${keyboardOpen ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center">
              <h1 className="text-lg sm:text-xl font-bold text-primary-600 dark:text-primary-400">
                TheProx Admin
              </h1>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <SurveyRatingChip />
              <ServerPaymentsChip />
              <button
                onClick={() => navigate('/settings')}
                className="p-2 rounded-lg bg-primary-50 dark:bg-gray-700 text-primary-600 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-gray-600 transition-colors"
                title="Настройки"
              >
                <SettingsIcon size={18} className="sm:w-5 sm:h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-primary-50 dark:bg-gray-700 text-primary-600 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-gray-600 transition-colors"
              >
                {isDark ? <Sun size={18} className="sm:w-5 sm:h-5" /> : <Moon size={18} className="sm:w-5 sm:h-5" />}
              </button>
              
              <div className="hidden sm:flex items-center space-x-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {user?.first_name || user?.firstName} {user?.last_name || user?.lastName}
                </span>
              </div>

              <button
                onClick={handleLogout}
                className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                title="Выйти"
                aria-label="Выйти"
              >
                <LogOut size={18} className="sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex">
        {/* Desktop Sidebar - Fixed */}
        <nav className="hidden lg:block w-64 bg-white dark:bg-gray-800 shadow-sm border-r border-primary-200 dark:border-gray-700 fixed left-0 top-14 sm:top-16 bottom-0 overflow-y-auto z-40">
          <div className="p-4">
            <nav className="space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <motion.button
                    key={item.name}
                    onClick={() => navigate(item.href)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-gray-700'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{item.name}</span>
                  </motion.button>
                );
              })}
            </nav>
          </div>
        </nav>

        {/* Page Content - With left margin for desktop sidebar */}
        <main className={`flex-1 lg:ml-64 p-3 sm:p-6 ${keyboardOpen ? 'pb-0' : 'pb-20'} lg:pb-6`}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>

      {/* Mobile Bottom Navigation — 6 пунктов: 5 основных + «Ещё» */}
      <nav className={`lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-primary-200 dark:border-gray-700 z-50 pb-safe ${keyboardOpen ? 'hidden' : ''}`}>
        <div className="flex items-stretch justify-around min-w-0 px-1 py-1.5">
          {mobilePrimary.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <motion.button
                key={item.name}
                onClick={() => navigate(item.href)}
                className={`flex flex-col items-center justify-center flex-1 min-w-0 max-w-[80px] py-1.5 px-0.5 rounded-lg transition-colors touch-manipulation ${
                  active
                    ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30'
                    : 'text-gray-600 dark:text-gray-400 active:bg-gray-100 dark:active:bg-gray-700'
                }`}
                whileTap={{ scale: 0.97 }}
                title={item.name}
              >
                <span className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${active ? 'bg-primary-100 dark:bg-primary-800/40' : ''}`}>
                  <Icon size={18} strokeWidth={2} aria-hidden />
                </span>
                <span className={`mt-1 text-[10px] font-medium text-center leading-tight truncate w-full block ${active ? 'text-primary-700 dark:text-primary-300' : ''}`}>
                  {item.name}
                </span>
              </motion.button>
            );
          })}
          <motion.button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 min-w-0 max-w-[80px] py-1.5 px-0.5 rounded-lg transition-colors touch-manipulation ${
              mobileMore.some((item) => isActive(item.href))
                ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30'
                : 'text-gray-600 dark:text-gray-400 active:bg-gray-100 dark:active:bg-gray-700'
            }`}
            whileTap={{ scale: 0.97 }}
            title="Ещё"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0">
              <Menu size={18} strokeWidth={2} aria-hidden />
            </span>
            <span className="mt-1 text-[10px] font-medium text-center leading-tight truncate w-full block">Ещё</span>
          </motion.button>
        </div>
      </nav>

      {/* Всплывающее окно «Ещё» с остальными пунктами */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-[60]"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden
            />
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="lg:hidden fixed left-0 right-0 bottom-0 z-[70] rounded-t-2xl bg-white dark:bg-gray-800 shadow-xl pb-safe max-h-[70vh] flex flex-col"
            >
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Разделы</h2>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-label="Закрыть"
                >
                  <X size={22} />
                </button>
              </div>
              <div className="overflow-y-auto px-3 pb-4">
                <div className="space-y-1">
                  {mobileMore.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    return (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => {
                          navigate(item.href);
                          setMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                          active
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-gray-700 shadow-sm">
                          <Icon size={20} />
                        </span>
                        <span className="font-medium">{item.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;
